package info.rezident.app.ui.components.profile

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.components.icons.fa.FaPlus
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.breakpoint.rememberBreakpoint
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import info.rezident.app.data.quiz.CompletedQuiz
import info.rezident.app.ui.theme.AppTheme
import org.jetbrains.compose.web.css.*

@Composable
fun ProfileBannerCard(
    displayName: String,
    initials: String,
    roleText: String?,
    isOnline: Boolean,
    modifier: Modifier = Modifier
) {
    val breakpoint = rememberBreakpoint()
    val isDark = ColorMode.current.isDark

    Box(
        modifier = modifier
            .fillMaxWidth()
            .run {
                if (isDark) {
                    backgroundColor(AppTheme.colors.onAccent)
                } else {
                    styleModifier {
                        property("background", "linear-gradient(135deg, #8ECAE6 0%, #219EBC 100%)")
                    }
                }
            }
            .borderRadius(AppTheme.shapes.bigRound)
            .padding(
                all = if (breakpoint < Breakpoint.MD) 20.px else 32.px
            ),
        contentAlignment = Alignment.CenterStart
    ) {
        Row(
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(16.px)
        ) {
            // Avatar placeholder
            Box(
                modifier = Modifier
                    .size(if (breakpoint < Breakpoint.MD) 60.px else 80.px)
                    .borderRadius(50.percent)
                    .backgroundColor(AppTheme.colors.surface)
                    .flexShrink(0)
                    .position(Position.Relative),
                contentAlignment = Alignment.Center
            ) {
                SpanText(
                    text = initials.uppercase(),
                    modifier = AppTheme.typography.h2.toModifier()
                        .color(AppTheme.colors.text)
                        .fontWeight(FontWeight.Bold)
                )

                // Online indicator
                if (isOnline) {
                    Box(
                        modifier = Modifier
                            .size(16.px)
                            .borderRadius(50.percent)
                            .backgroundColor(Color("#22c55e"))
                            .border(2.px, LineStyle.Solid, AppTheme.colors.surface)
                            .position(Position.Absolute)
                            .bottom(4.px)
                            .right(4.px)
                    )
                }
            }

            Column {
                SpanText(
                    text = displayName,
                    modifier = AppTheme.typography.h1.toModifier()
                        .color(AppTheme.colors.text)
                        .fontWeight(FontWeight.Bold)
                        .margin(bottom = 4.px)
                )

                roleText?.let { role ->
                    SpanText(
                        text = role,
                        modifier = AppTheme.typography.body1.toModifier()
                            .color(AppTheme.colors.textSecondary)
                    )
                }
            }
        }
    }
}

@Composable
fun AccountInformationCard(
    email: String?,
    memberSince: String?,
    academicYear: String?,
    modifier: Modifier = Modifier
) {
    Column(
        modifier = modifier
            .fillMaxWidth()
            .backgroundColor(AppTheme.colors.surface)
            .borderRadius(AppTheme.shapes.mediumRound)
            .border(1.px, LineStyle.Solid, AppTheme.colors.textTertiary)
            .padding(20.px)
    ) {
        Row(
            verticalAlignment = Alignment.CenterVertically,
            modifier = Modifier.margin(bottom = 16.px)
        ) {
            SpanText(
                text = "👤",
                modifier = Modifier.fontSize(20.px).margin(right = 8.px)
            )
            SpanText(
                text = "Profilul meu",
                modifier = AppTheme.typography.h2.toModifier()
                    .color(AppTheme.colors.text)
                    .fontWeight(FontWeight.SemiBold)
            )
        }

        // Email row
        if (email != null) {
            InfoRow(
                icon = "✉️",
                label = "E-mail",
                value = email
            )
            Divider()
        }

        // Member Since row
        if (memberSince != null) {
            InfoRow(
                icon = "📅",
                label = "Membru din data",
                value = memberSince
            )
            if (academicYear != null) Divider()
        }

        // Academic Year row
        if (academicYear != null) {
            InfoRow(
                icon = "🎓",
                label = "Anul de studii",
                value = academicYear
            )
        }
    }
}

@Composable
private fun InfoRow(
    icon: String,
    label: String,
    value: String
) {
    Row(
        modifier = Modifier.fillMaxWidth().padding(topBottom = 12.px),
        verticalAlignment = Alignment.Top
    ) {
        SpanText(
            text = icon,
            modifier = Modifier.fontSize(16.px).margin(right = 12.px)
        )
        Column {
            SpanText(
                text = label,
                modifier = AppTheme.typography.body2.toModifier()
                    .color(AppTheme.colors.textSecondary)
                    .margin(bottom = 2.px)
            )
            SpanText(
                text = value,
                modifier = AppTheme.typography.body1.toModifier()
                    .color(AppTheme.colors.text)
            )
        }
    }
}

@Composable
private fun Divider() {
    Box(
        modifier = Modifier
            .fillMaxWidth()
            .height(1.px)
            .backgroundColor(AppTheme.colors.textTertiary)
            .margin(top = 4.px, bottom = 4.px)
    )
}

@Composable
fun OrientationQuizzesSection(
    quizzes: List<CompletedQuiz>,
    completedCount: Int,
    onQuizClick: (String) -> Unit,
    onStartNewQuizClick: () -> Unit,
    modifier: Modifier = Modifier
) {
    Column(
        modifier = modifier.fillMaxWidth()
    ) {
        // Section header
        Row(
            modifier = Modifier.fillMaxWidth().margin(topBottom = 16.px),
            horizontalArrangement = Arrangement.SpaceBetween,
            verticalAlignment = Alignment.CenterVertically
        ) {
            Row(verticalAlignment = Alignment.CenterVertically) {
                SpanText(
                    text = "🧭",
                    modifier = Modifier.fontSize(20.px).margin(right = 8.px)
                )
                SpanText(
                    text = "Quiz-uri",
                    modifier = AppTheme.typography.h2.toModifier()
                        .color(AppTheme.colors.text)
                        .fontWeight(FontWeight.SemiBold)
                )
            }

            CompletedBadge(completedCount)
        }

        // Quiz cards
        Column(
            Modifier.fillMaxWidth(),
            verticalArrangement = Arrangement.spacedBy(24.px)
        ) {
            TakeAnotherQuizCard(onClick = onStartNewQuizClick)

            quizzes.forEach { quiz ->
                QuizRowCard(
                    quiz = quiz,
                    onClick = { onQuizClick(quiz.quizId) }
                )
            }
        }
    }
}

@Composable
private fun CompletedBadge(count: Int) {
    Box(
        modifier = Modifier
            .backgroundColor(AppTheme.colors.section)
            .borderRadius(20.px)
            .padding(leftRight = 12.px, topBottom = 6.px)
    ) {
        SpanText(
            text = if (count == 1)
                "$count completat"
            else
                "$count completate",
            modifier = AppTheme.typography.body2.toModifier()
                .color(AppTheme.colors.text)
                .fontWeight(FontWeight.Medium)
        )
    }
}

@Composable
fun QuizRowCard(
    quiz: CompletedQuiz,
    onClick: () -> Unit,
    modifier: Modifier = Modifier
) {
    val hasRecommendations = quiz.recommendations != null

    Row(
        modifier = modifier
            .fillMaxWidth()
            .backgroundColor(AppTheme.colors.surface)
            .borderRadius(AppTheme.shapes.mediumRound)
            .border(1.px, LineStyle.Solid, AppTheme.colors.textTertiary)
            .padding(16.px)
            .cursor(Cursor.Pointer)
            .onClick { onClick() },
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.SpaceBetween
    ) {
        Row(
            verticalAlignment = Alignment.Top,
            horizontalArrangement = Arrangement.spacedBy(12.px)
        ) {
            // Quiz type icon
            SpanText(
                text = "📋",
                modifier = Modifier.fontSize(20.px)
            )

            Column {
                Row(
                    verticalAlignment = Alignment.CenterVertically,
                    horizontalArrangement = Arrangement.spacedBy(8.px)
                ) {
                    SpanText(
                        text = getQuizDisplayName(),
                        modifier = AppTheme.typography.body1.toModifier()
                            .color(AppTheme.colors.text)
                            .fontWeight(FontWeight.SemiBold)
                    )

                    SpanText(
                        text = getQuizVersionTag(quiz.templateId),
                        modifier = AppTheme.typography.body2.toModifier()
                            .color(AppTheme.colors.textSecondary)
                            .fontSize(12.px)
                            .padding(leftRight = 6.px, topBottom = 2.px)
                            .backgroundColor(AppTheme.colors.section)
                            .borderRadius(4.px)
                    )
                }

                StatusChip(
                    isUnlocked = quiz.isUnlocked,
                    modifier = Modifier.margin(bottom = 8.px)
                )

                SpanText(
                    text = if (hasRecommendations) {
                        "Completat pe ${formatQuizDate(quiz.createdAt)}"
                    } else {
                        "${quiz.answers.size} răspunsuri"
                    },
                    modifier = AppTheme.typography.body2.toModifier()
                        .color(AppTheme.colors.textTertiary)
                )
            }
        }

        // Chevron
        SpanText(
            text = "›",
            modifier = Modifier
                .fontSize(24.px)
                .color(AppTheme.colors.textTertiary)
        )
    }
}

@Composable
private fun StatusChip(
    isUnlocked: Boolean,
    modifier: Modifier = Modifier
) {
    Box(
        modifier = modifier
            .backgroundColor(
                if (isUnlocked) Color("#22c55e") else AppTheme.colors.section
            )
            .borderRadius(12.px)
            .padding(leftRight = 8.px, topBottom = 4.px)
    ) {
        SpanText(
            text = if (isUnlocked) {
                "Analiză Detaliată"
            } else {
                "Recomandări"
            },
            modifier = AppTheme.typography.body2.toModifier()
                .color(
                    if (isUnlocked) Colors.White else AppTheme.colors.text
                )
                .fontSize(12.px)
                .fontWeight(FontWeight.Medium)
        )
    }
}

@Composable
fun TakeAnotherQuizCard(
    onClick: () -> Unit,
    modifier: Modifier = Modifier
) {
    Row(
        modifier = modifier
            .fillMaxWidth()
            .backgroundColor(AppTheme.colors.section)
            .borderRadius(AppTheme.shapes.mediumRound)
            .border(1.px, LineStyle.Solid, AppTheme.colors.accent)
            .padding(16.px)
            .cursor(Cursor.Pointer)
            .onClick { onClick() },
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.SpaceBetween
    ) {
        Row(
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(12.px)
        ) {
            Box(
                modifier = Modifier
                    .size(40.px)
                    .borderRadius(50.percent)
                    .backgroundColor(AppTheme.colors.accent)
                    .flexShrink(0)
                    .border(2.px, LineStyle.Solid, AppTheme.colors.surface),
                contentAlignment = Alignment.Center
            ) {
                FaPlus(Modifier.color(AppTheme.colors.onAccent))
            }

            Column {
                SpanText(
                    text = "Încearcă un nou Quiz de Orientare",
                    modifier = AppTheme.typography.body1.toModifier()
                        .color(AppTheme.colors.text)
                        .fontWeight(FontWeight.SemiBold)
                        .margin(bottom = 2.px)
                )
                SpanText(
                    text = "Descoperă mai multe recomandări de specialități",
                    modifier = AppTheme.typography.body2.toModifier()
                        .color(AppTheme.colors.textSecondary)
                )
            }
        }

        SpanText(
            text = "›",
            modifier = Modifier
                .fontSize(24.px)
                .color(AppTheme.colors.textTertiary)
        )
    }
}

private fun getQuizDisplayName(): String {
    return "Quiz de Orientare"
}

private fun getQuizVersionTag(templateId: Int): String {
    return when (templateId) {
        1 -> "v1 (Beta)"
        2 -> "v2"
        else -> "v$templateId"
    }
}

private fun formatQuizDate(isoString: String): String {
    return try {
        val parsedDate: kotlin.js.Date? = run {
            val d = kotlin.js.Date(isoString)
            val t: Double = d.getTime()
            if (!t.isNaN()) d else null
        } ?: run {
            val numeric: Double? = isoString.toDoubleOrNull()
            if (numeric != null) {
                val ms: Double = if (numeric < 1_000_000_000_000.0) numeric * 1000.0 else numeric
                kotlin.js.Date(ms)
            } else null
        }

        if (parsedDate == null) return "-"

        val day: String = parsedDate.getDate().toString().padStart(2, '0')
        val month: String = (parsedDate.getMonth() + 1).toString().padStart(2, '0')
        val year: Int = parsedDate.getFullYear()
        "$day ${monthNumberToString(month)} $year"
    } catch (e: Throwable) {
        "-"
    }
}

private fun monthNumberToString(month: String) =
    when (month) {
        "01" -> "ianuarie"
        "02" -> "februarie"
        "03" -> "martie"
        "04" -> "aprilie"
        "05" -> "mai"
        "06" -> "iunie"
        "07" -> "iulie"
        "08" -> "august"
        "09" -> "septembrie"
        "10" -> "octombrie"
        "11" -> "noiembrie"
        "12" -> "decembrie"
        else -> "-"
    }